import { BaseModelWithFiles } from "@/models/base-with-files.model"
import { ApprovalProcessingSource } from "@/enums/approval-processing-source.enum"
import type { IProductionStatementApproval } from "@/interfaces/api/production-statement-approval.interface"
import { KnownBusinessEntity } from "@/models/known-business-entity.model"
import { ModelFactory } from "@/factories/model-factory"
import { ProductionStatementApprovalAllocation } from "@/models/production-statement-approval-allocation.model"
import type { FileRelation } from "@/models/file/file-relation.model.ts"
import type { Moment } from "moment"
import { ApprovalMode } from "@/enums/approval-mode.enum"
import {
  productionStatementApprovalReferenceMatchesFormat,
  productionStatementApprovalReferenceFormatError
} from "@/helper/validation/production-statement-approval-reference-format"

import { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model"
import { OutgoingInvoice } from "@/models/outgoing-invoice.model"

export class ProductionStatementApproval
  extends BaseModelWithFiles
  implements IProductionStatementApproval
{
  public static className = "ProductionStatementApproval"
  public static verboseName = "Klantakkoord"

  supplierName?: string
  supplier?: KnownBusinessEntity | null
  reference?: string
  externalReference: string | null
  externalReference2: string
  amountExclVat: string | null
  approvalDate: Moment | null
  processedAt: Moment | null
  processingSource: ApprovalProcessingSource | null
  processedBy: KnownBusinessEntityContact | null
  commentInternal: string
  commentInvoice: string
  productionStatementApprovalAllocations: ProductionStatementApprovalAllocation[]
  outgoingInvoice: OutgoingInvoice | null
  outgoingInvoices: OutgoingInvoice[]
  invoicedSum: string
  deletedAt: Moment | null
  deletedBy: KnownBusinessEntityContact | null

  constructor(productionStatementApproval: IProductionStatementApproval, managed = false) {
    super(productionStatementApproval, managed)

    this.supplierName = productionStatementApproval.supplierName
    this.supplier = ModelFactory.childModel(
      KnownBusinessEntity,
      productionStatementApproval.supplier,
      managed
    )
    this.reference = productionStatementApproval.reference
    this.externalReference = productionStatementApproval.externalReference ?? null
    this.externalReference2 = productionStatementApproval.externalReference2 ?? ""
    this.amountExclVat = productionStatementApproval.amountExclVat ?? null
    this.approvalDate = productionStatementApproval.approvalDate ?? null
    this.processedAt = productionStatementApproval.processedAt ?? null
    this.processingSource = productionStatementApproval.processingSource ?? null
    this.processedBy =
      ModelFactory.childModel(
        KnownBusinessEntityContact,
        productionStatementApproval.processedBy,
        managed
      ) ?? null
    this.commentInternal = productionStatementApproval.commentInternal ?? ""
    this.commentInvoice = productionStatementApproval.commentInvoice ?? ""
    this.productionStatementApprovalAllocations = ModelFactory.childModels(
      ProductionStatementApprovalAllocation,
      productionStatementApproval.productionStatementApprovalAllocations ?? [],
      managed
    )
    // Managed like every nested model: a socket update of the invoice itself lands on this same instance.
    this.outgoingInvoice =
      ModelFactory.childModel(
        OutgoingInvoice,
        productionStatementApproval.outgoingInvoice,
        managed
      ) ?? null
    this.outgoingInvoices = ModelFactory.childModels(
      OutgoingInvoice,
      productionStatementApproval.outgoingInvoices ?? [],
      managed
    )
    this.invoicedSum = productionStatementApproval.invoicedSum ?? "0"
    this.deletedAt = productionStatementApproval.deletedAt ?? null
    this.deletedBy =
      ModelFactory.childModel(
        KnownBusinessEntityContact,
        productionStatementApproval.deletedBy,
        managed
      ) ?? null
  }

  public isSystemProcessed(): boolean {
    return this.processingSource === ApprovalProcessingSource.SYSTEM
  }

  public isDeleted(): boolean {
    return this.deletedAt !== null
  }

  /** An open invoice exists: the allocations are what it says and cannot change (PNTS-3221). */
  public isInvoiced(): boolean {
    return this.outgoingInvoice !== null
  }

  /** The active invoice is finished, either sent or deliberately not sent. */
  public isInvoiceFinished(): boolean {
    const invoice = this.outgoingInvoice
    return !!invoice && invoice.isFinished && !invoice.isCredited && !invoice.isCredit
  }

  /** Invoiced and the invoice was mailed: the "Gefactureerd" chip (PNTS-3227). */
  public isInvoiceSent(): boolean {
    return this.outgoingInvoice?.sentAt != null
  }

  /**
   * Every invoice that concerns this approval, oldest first: each invoice made for it followed by the credit
   * invoice cancelling it (PNTS-3220). The open invoice, if any, is the last one.
   */
  public getInvoiceHistory(): OutgoingInvoice[] {
    const byAge = (a: OutgoingInvoice, b: OutgoingInvoice) =>
      (a.createdAt?.valueOf() ?? 0) - (b.createdAt?.valueOf() ?? 0)
    return [...this.outgoingInvoices]
      .sort(byAge)
      .flatMap((invoice) => (invoice.creditInvoice ? [invoice, invoice.creditInvoice] : [invoice]))
  }

  /** The history without the open invoice: credited invoices and their credits. */
  public getPastInvoices(): OutgoingInvoice[] {
    return this.getInvoiceHistory().filter((invoice) => invoice.id !== this.outgoingInvoice?.id)
  }

  /** An invoice was mailed at some point, credited since or not: the "Gefactureerd" chip (PNTS-3227). */
  public hasSentInvoice(): boolean {
    return (
      this.isInvoiceSent() || this.getInvoiceHistory().some((invoice) => invoice.sentAt != null)
    )
  }

  /** An invoice is underway: shown in the "Verzenden" panel, in no chip (PNTS-3227). */
  public hasInvoiceInProgress(): boolean {
    return this.outgoingInvoice !== null && !this.outgoingInvoice.isFinished
  }

  public getInvoiceSentAt(): Moment | null {
    return this.outgoingInvoice?.sentAt ?? null
  }

  /** Processed, open (no invoice) and alive: ready for the Factureren chip and the Factureren button. */
  public isReadyToInvoice(): boolean {
    return !!this.processedAt && !this.isInvoiced() && !this.isDeleted()
  }

  public hasApprovalContent(): boolean {
    return Boolean(
      this.supplier ||
      this.approvalDate ||
      this.hasSourceAmount() ||
      this.externalReference?.trim() ||
      this.externalReference2.trim() ||
      this.commentInternal.trim() ||
      this.commentInvoice.trim() ||
      this.fileBucket.fileRelations.length
    )
  }

  public getSupplierName(): string {
    return this.supplier?.name || this.supplierName || ""
  }

  public getAllocatedAmount(): number {
    return this.productionStatementApprovalAllocations.reduce(
      (sum, allocation) => sum + Number(allocation.amount),
      0
    )
  }

  public getOutstandingAmount(): number {
    return Number((Number(this.amountExclVat) - this.getAllocatedAmount()).toFixed(8))
  }

  public isOutstanding(): boolean {
    return this.getOutstandingAmount() !== 0
  }

  public getFileRelation(): FileRelation | undefined {
    return this.fileBucket?.fileRelations[0]
  }

  public hasSourceAmount(): boolean {
    return this.amountExclVat !== null && this.amountExclVat !== ""
  }

  public getReferenceValidationErrors(): {
    externalReference: string[]
    externalReference2: string[]
  } {
    const requirements = this.getApprovalRequirements()
    const validate = (
      value: string | null,
      required: boolean,
      format: string | null,
      label: string
    ) => {
      if (required && !value?.trim()) return [`${label} is verplicht.`]
      return productionStatementApprovalReferenceMatchesFormat(value, format)
        ? []
        : [productionStatementApprovalReferenceFormatError(format)]
    }
    return {
      externalReference: validate(
        this.externalReference,
        requirements.referenceRequired,
        requirements.referenceFormat,
        requirements.referenceLabel || "Referentie 1"
      ),
      externalReference2: validate(
        this.externalReference2,
        requirements.reference2Required,
        requirements.reference2Format,
        requirements.reference2Label || "Referentie 2"
      )
    }
  }

  public getApprovalRequirements() {
    const client = this.supplier
    const noApprovalRequired = client?.approvalMode !== ApprovalMode.REQUIRED
    return {
      amountRequired: !noApprovalRequired && (client?.approvalAmountRequired ?? false),
      fileRequired: !noApprovalRequired && (client?.approvalFileRequired ?? false),
      referenceRequired:
        !noApprovalRequired &&
        Boolean(client?.approvalReferenceLabel.trim()) &&
        (client?.approvalReferenceRequired ?? false),
      referenceLabel: noApprovalRequired ? "" : (client?.approvalReferenceLabel ?? ""),
      referenceFormat:
        noApprovalRequired || !client?.approvalReferenceLabel.trim()
          ? null
          : client.approvalReferenceFormat,
      reference2Required:
        !noApprovalRequired &&
        Boolean(client?.approvalReference2Label.trim()) &&
        (client?.approvalReference2Required ?? false),
      reference2Label: noApprovalRequired ? "" : (client?.approvalReference2Label ?? ""),
      reference2Format:
        noApprovalRequired || !client?.approvalReference2Label.trim()
          ? null
          : client.approvalReference2Format
    }
  }
}
